import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { getGuestCommerceToken } from "./guestCommerce";
import "./MarketplaceCheckout.css";

const money = (value, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(Number(value || 0));

const emptyForm = {
  email: "", name: "", phone: "",
  address1: "", address2: "", city: "", region: "",
  postalCode: "", country: "US",
};

export default function MarketplaceCheckout() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const orderIdFromUrl = params.get("order");
  const offerThreadIdFromUrl = params.get("offer");
  const paypalReturn = params.get("paypal");
  const [session, setSession] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [order, setOrder] = useState(null);
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const paypalCaptureStarted = useRef(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        if (!alive) return;
        const nextSession = sessionData?.session || null;
        setSession(nextSession);
        if (nextSession?.user?.email) {
          setForm((v) => ({ ...v, email: nextSession.user.email }));
        }

        if (orderIdFromUrl) {
          const rpc = nextSession?.user?.id ? "get_my_checkout_summary" : "get_guest_checkout_summary";
          const args = nextSession?.user?.id
            ? { p_order_id: orderIdFromUrl }
            : { p_guest_token: getGuestCommerceToken(), p_order_id: orderIdFromUrl };
          const { data, error } = await supabase.rpc(rpc, args);
          if (error) throw error;
          setOrder(data);
        } else if (offerThreadIdFromUrl) {
          if (!nextSession?.user?.id) {
            throw new Error("Sign in is required to check out an accepted offer.");
          }
          const { data, error } = await supabase.rpc("get_my_marketplace_offer_detail", {
            p_offer_thread_id: offerThreadIdFromUrl,
          });
          if (error) throw error;
          const offer = data || null;
          if (!offer || offer.status !== "accepted" || !offer.accepted_amount) {
            throw new Error("This offer is not ready for checkout.");
          }
          setCart({
            source: "accepted_offer",
            offer_thread_id: offerThreadIdFromUrl,
            currency: offer.currency || "USD",
            items: [{
              listing_id: offer.listing_id,
              title: offer.title || "Marketplace item",
              image_url: offer.image_url || null,
              condition: offer.condition || null,
              quantity: 1,
              price_amount: offer.accepted_amount,
              currency: offer.currency || "USD",
              shop_name: offer.shop_name || "Hobbli Shop",
            }],
          });
        } else {
          const pendingRpc = nextSession?.user?.id
            ? "get_my_pending_marketplace_checkout"
            : "get_guest_pending_marketplace_checkout";
          const pendingArgs = nextSession?.user?.id
            ? {}
            : { p_guest_token: getGuestCommerceToken() };
          const { data: pendingOrder, error: pendingError } = await supabase.rpc(pendingRpc, pendingArgs);
          if (pendingError) throw pendingError;

          if (pendingOrder?.order_id) {
            setOrder(pendingOrder);
            navigate(`/checkout?order=${pendingOrder.order_id}`, { replace: true });
          } else {
            const rpc = nextSession?.user?.id ? "get_my_cart_summary" : "get_guest_cart_summary";
            const args = nextSession?.user?.id ? {} : { p_guest_token: getGuestCommerceToken() };
            const { data, error } = await supabase.rpc(rpc, args);
            if (error) throw error;
            setCart(data || { items: [] });
          }
        }
      } catch (error) {
        console.error("Checkout load error:", error);
        setNotice({ type: "error", message: error?.message || "Unable to load checkout." });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [orderIdFromUrl, offerThreadIdFromUrl, navigate]);

  const cartItems = Array.isArray(cart?.items) ? cart.items : [];
  const cartSubtotal = useMemo(
    () => cartItems.reduce((sum, row) => sum + Number(row.price_amount || 0) * Number(row.quantity || 0), 0),
    [cartItems]
  );

  const update = (key) => (event) =>
    setForm((value) => ({ ...value, [key]: event.target.value }));

  const beginCheckout = async (event) => {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      const shipping = {
        address1: form.address1.trim(),
        address2: form.address2.trim() || null,
        city: form.city.trim(),
        region: form.region.trim(),
        postal_code: form.postalCode.trim(),
        country: form.country.trim().toUpperCase(),
      };
      const contact = {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim().toLowerCase() || null,
      };

      const isAcceptedOffer = cart?.source === "accepted_offer" && cart?.offer_thread_id;
      const rpc = isAcceptedOffer
        ? "begin_accepted_offer_checkout_v2"
        : session?.user?.id
          ? "begin_marketplace_checkout_v2"
          : "begin_guest_marketplace_checkout";
      const args = isAcceptedOffer
        ? {
            p_offer_thread_id: cart.offer_thread_id,
            p_shipping_address: shipping,
            p_buyer_contact: contact,
          }
        : session?.user?.id
          ? { p_shipping_address: shipping, p_buyer_contact: contact }
          : {
              p_guest_token: getGuestCommerceToken(),
              p_email: form.email.trim().toLowerCase(),
              p_shipping_address: shipping,
              p_buyer_contact: contact,
            };

      const { data, error } = await supabase.rpc(rpc, args);
      if (error) throw error;
      navigate(`/checkout?order=${data}`, { replace: true });
    } catch (error) {
      console.error("Begin checkout error:", error);
      setNotice({ type: "error", message: error?.message || "Unable to begin checkout." });
    } finally {
      setBusy(false);
    }
  };

  const startPayPalCheckout = async () => {
    if (!order?.order_id) return;
    setBusy(true);
    setNotice(null);
    try {
      if (!session?.access_token) {
        throw new Error("Sign in is required for this PayPal sandbox checkpoint.");
      }

      const { data, error } = await supabase.functions.invoke("paypal-marketplace", {
        body: { action: "create-checkout-order", orderId: order.order_id },
      });
      if (error) throw error;
      if (!data?.success || !data?.approvalUrl) {
        throw new Error(data?.error || "PayPal checkout could not be started.");
      }

      window.location.assign(data.approvalUrl);
    } catch (error) {
      console.error("PayPal checkout start error:", error);
      setNotice({ type: "error", message: error?.message || "Unable to start PayPal checkout." });
      setBusy(false);
    }
  };


  useEffect(() => {
    if (
      paypalReturn !== "return" ||
      !order?.order_id ||
      ["placed", "completed", "cancelled", "expired"].includes(order?.status) ||
      paypalCaptureStarted.current
    ) return;

    paypalCaptureStarted.current = true;
    let alive = true;

    (async () => {
      setBusy(true);
      setNotice({ type: "info", message: "PayPal approved the payment. Hobbli is securely capturing and finalizing your order…" });
      try {
        if (!session?.access_token) {
          throw new Error("Sign in is required to finalize this PayPal sandbox payment.");
        }

        const { data, error } = await supabase.functions.invoke("paypal-marketplace", {
          body: { action: "capture-checkout-order", orderId: order.order_id },
        });
        if (error) throw error;
        if (!data?.success) {
          throw new Error(data?.error || "PayPal payment could not be finalized.");
        }

        const { data: refreshed, error: refreshError } = await supabase.rpc(
          "get_my_checkout_summary",
          { p_order_id: order.order_id },
        );
        if (refreshError) throw refreshError;
        if (!alive) return;

        setOrder(refreshed);
        setNotice({ type: "success", message: "Payment captured successfully. Your Hobbli order has been placed." });
        navigate(`/checkout?order=${order.order_id}`, { replace: true });
      } catch (error) {
        console.error("PayPal checkout capture error:", error);
        if (!alive) return;
        setNotice({
          type: "error",
          message: error?.message || "PayPal payment could not be finalized. Do not submit another payment until the order is checked.",
        });
      } finally {
        if (alive) setBusy(false);
      }
    })();

    return () => { alive = false; };
  }, [paypalReturn, order?.order_id, order?.status, session?.access_token, navigate]);

  const cancelCheckout = async () => {
    if (!order?.order_id) return;
    setBusy(true);
    setNotice(null);
    try {
      const rpc = session?.user?.id
        ? "cancel_pending_marketplace_order"
        : "cancel_guest_marketplace_checkout";
      const args = session?.user?.id
        ? { p_order_id: order.order_id }
        : { p_guest_token: getGuestCommerceToken(), p_order_id: order.order_id };
      const { error } = await supabase.rpc(rpc, args);
      if (error) throw error;
      navigate("/shop", { replace: true });
    } catch (error) {
      setNotice({ type: "error", message: error?.message || "Unable to cancel checkout." });
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <main className="market-checkout-page"><div className="market-checkout-state">Loading checkout…</div></main>;

  if (order) {
    const sellerOrders = Array.isArray(order.seller_orders) ? order.seller_orders : [];
    const orderItems = Array.isArray(order.items) ? order.items : [];
    const expires = order.reservation_expires_at ? new Date(order.reservation_expires_at) : null;
    const allReady = sellerOrders.length > 0 && sellerOrders.every((row) => row.payment_ready);
    const isPaid = ["placed", "completed"].includes(order.status);
    const isCancelled = order.status === "cancelled";
    const heading = isPaid ? "Receipt" : isCancelled ? "Cancelled Order" : "Order Summary";
    const grouped = sellerOrders.map((seller) => ({
      ...seller,
      items: orderItems.filter((item) => item.seller_order_id === seller.seller_order_id),
    }));
    const address = order.shipping_address || {};
    const contact = order.buyer_contact || {};

    return (
      <main className="market-checkout-page">
        <div className="market-checkout-shell market-receipt-shell">
          <header className="market-checkout-heading market-no-print">
            <div>
              <span>Hobbli Marketplace</span>
              <h1>{heading}</h1>
              {order.order_number && <small className="market-order-number">{order.order_number}</small>}
            </div>
            <div className="market-checkout-actions">
              <button onClick={() => window.print()}>Print / Save PDF</button>
              <button onClick={() => navigate("/shop")}>Continue Shopping</button>
            </div>
          </header>

          <div className="market-print-header">
            <strong>Hobbli Marketplace</strong>
            <h1>{heading}</h1>
            <span>{order.order_number}</span>
          </div>

          {notice && <div className={`market-checkout-notice ${notice.type}`}>{notice.message}</div>}
          {paypalReturn === "cancel" && !isPaid && !isCancelled && (
            <div className="market-checkout-notice info">
              PayPal checkout was cancelled. Your Hobbli reservation remains pending until it expires or you cancel it.
            </div>
          )}

          {!isPaid && !isCancelled && (
            <div className="market-checkout-reserved">
              <strong>Pending payment — your items are reserved.</strong>
              <span>{expires ? `Reservation expires ${expires.toLocaleString()}.` : ""}</span>
            </div>
          )}
          {isCancelled && (
            <div className="market-checkout-notice info">
              This order was cancelled. No payment was completed.
            </div>
          )}

          <section className="market-receipt-meta">
            <div>
              <span>Order</span>
              <strong>{order.order_number || order.order_id}</strong>
              <small>{order.created_at ? new Date(order.created_at).toLocaleString() : ""}</small>
            </div>
            <div>
              <span>Ship to</span>
              <strong>{contact.name || "Buyer"}</strong>
              <small>
                {[address.address1, address.address2, address.city, address.region, address.postal_code, address.country]
                  .filter(Boolean).join(", ")}
              </small>
            </div>
          </section>

          <div className="market-checkout-layout">
            <section className="market-checkout-card market-order-items-card">
              <h2>Items in your order</h2>

              {grouped.map((seller) => (
                <div className="market-order-shop" key={seller.seller_order_id}>
                  <div className="market-order-shop-heading">
                    <div>
                      <span>Sold by</span>
                      <strong>{seller.shop_name}</strong>
                    </div>
                    <strong>{money(seller.total, order.currency)}</strong>
                  </div>

                  {seller.items.map((item) => (
                    <div className="market-order-item" key={item.order_item_id}>
                      <div className="market-order-item-image">
                        {item.image_url
                          ? <img src={item.image_url} alt="" />
                          : <div className="market-order-image-placeholder">H</div>}
                      </div>
                      <div className="market-order-item-copy">
                        <strong>{item.title}</strong>
                        {item.condition && <span>Condition: {item.condition}</span>}
                        <span>Quantity: {item.quantity}</span>
                        {Number(item.shipping_amount || 0) === 0
                          ? <span>Free shipping</span>
                          : <span>Shipping: {money(item.shipping_amount, order.currency)}</span>}
                      </div>
                      <div className="market-order-item-price">
                        <strong>{money(Number(item.unit_price || 0) * Number(item.quantity || 0), order.currency)}</strong>
                        {Number(item.quantity || 0) > 1 && <small>{money(item.unit_price, order.currency)} each</small>}
                      </div>
                    </div>
                  ))}

                  <div className="market-order-shop-totals">
                    <span>Items {money(seller.items_subtotal, order.currency)}</span>
                    <span>Shipping {money(seller.shipping_total, order.currency)}</span>
                  </div>

                  {!isPaid && !isCancelled && (
                    <div className="market-payment-group">
                      <small>
                        {seller.payment_ready
                          ? `${seller.payment_provider} is connected and ready for payment.`
                          : "This shop does not have a live payment provider connected yet."}
                      </small>
                    </div>
                  )}
                </div>
              ))}

              {!isPaid && !isCancelled && (
                <div className="market-checkout-boundary market-no-print">
                  {allReady
                    ? "PayPal Sandbox checkout is enabled for the current Admin Only test."
                    : "Checkout and inventory reservation are working. No money has been charged."}
                </div>
              )}
            </section>

            <aside className="market-checkout-summary">
              <span>{isPaid ? "Receipt total" : "Order total"}</span>
              <div><b>Items</b><strong>{money(order.items_subtotal, order.currency)}</strong></div>
              <div><b>Shipping</b><strong>{money(order.shipping_total, order.currency)}</strong></div>
              {Number(order.tax_total || 0) !== 0 && <div><b>Tax</b><strong>{money(order.tax_total, order.currency)}</strong></div>}
              {Number(order.discount_total || 0) !== 0 && <div><b>Discount</b><strong>-{money(order.discount_total, order.currency)}</strong></div>}
              <hr />
              <div className="market-checkout-total"><b>Total</b><strong>{money(order.grand_total, order.currency)}</strong></div>

              {!isPaid && !isCancelled && allReady && (
                <button
                  disabled={busy}
                  className="market-checkout-primary market-no-print"
                  onClick={startPayPalCheckout}
                >
                  {busy ? "Opening PayPal Sandbox…" : "Pay with PayPal Sandbox"}
                </button>
              )}

              <button className="market-checkout-secondary market-no-print" onClick={() => window.print()}>
                Print / Save PDF
              </button>

              {!isPaid && !isCancelled && (
                <button disabled={busy} className="market-checkout-secondary market-no-print" onClick={cancelCheckout}>
                  Cancel Checkout
                </button>
              )}

              {!isPaid && !isCancelled && <p>Hobbli fees are seller-side and are not added to your total.</p>}
              {isPaid && <p>Keep this receipt for your records.</p>}
            </aside>
          </div>
        </div>
      </main>
    );
  }

  if (!cartItems.length) {
    return (
      <main className="market-checkout-page">
        <div className="market-checkout-state">
          <h2>Your cart is empty.</h2>
          <button onClick={() => navigate("/shop")}>Browse Marketplace</button>
        </div>
      </main>
    );
  }

  return (
    <main className="market-checkout-page">
      <div className="market-checkout-shell">
        <header className="market-checkout-heading">
          <div><span>Hobbli Marketplace</span><h1>{cart?.source === "accepted_offer" ? "Offer Checkout" : "Checkout"}</h1></div>
          <button onClick={() => navigate(cart?.source === "accepted_offer" ? `/orders?tab=offers&offer=${cart.offer_thread_id}` : "/cart")}>{cart?.source === "accepted_offer" ? "Back to offer" : "Back to cart"}</button>
        </header>

        {notice && <div className={`market-checkout-notice ${notice.type}`}>{notice.message}</div>}

        <form className="market-checkout-layout" onSubmit={beginCheckout}>
          <section className="market-checkout-card">
            <h2>Contact & shipping</h2>
            {!session?.user?.id && (
              <label>Email<input type="email" required value={form.email} onChange={update("email")} /></label>
            )}
            <label>Full name<input required value={form.name} onChange={update("name")} /></label>
            <label>Phone <small>optional</small><input value={form.phone} onChange={update("phone")} /></label>
            <label>Street address<input required value={form.address1} onChange={update("address1")} /></label>
            <label>Apartment, suite, etc. <small>optional</small><input value={form.address2} onChange={update("address2")} /></label>
            <div className="market-checkout-grid">
              <label>City<input required value={form.city} onChange={update("city")} /></label>
              <label>State / region<input required value={form.region} onChange={update("region")} /></label>
              <label>ZIP / postal code<input required value={form.postalCode} onChange={update("postalCode")} /></label>
              <label>Country<input required value={form.country} onChange={update("country")} /></label>
            </div>
          </section>

          <aside className="market-checkout-summary">
            <span>Review</span>
            <div><b>{cart?.source === "accepted_offer" ? "Accepted offer" : `${cartItems.length} cart item${cartItems.length === 1 ? "" : "s"}`}</b><strong>{money(cartSubtotal, cart?.currency || "USD")}</strong></div>
            <p>{cart?.source === "accepted_offer" ? "This item is already reserved from your accepted offer. Continue to create the order and payment checkout." : "Availability and pricing are revalidated when you continue. Inventory is reserved only after checkout begins."}</p>
            <button disabled={busy} className="market-checkout-primary" type="submit">
              {busy ? (cart?.source === "accepted_offer" ? "Creating order…" : "Reserving items…") : (cart?.source === "accepted_offer" ? "Review accepted offer" : "Review & reserve items")}
            </button>
            {!session?.user?.id && <p>No Hobbli account required.</p>}
          </aside>
        </form>
      </div>
    </main>
  );
}
